/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import { useBus } from '@accelint/bus/react';
import { uuid } from '@accelint/core';
import { useToastRegion } from 'react-aria/useToast';
import { useToastQueue } from 'react-stately/useToastState';
import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  type QueuedToast,
  UNSTABLE_ToastList as ToastList,
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastRegion as ToastRegion,
  UNSTABLE_ToastStateContext as ToastStateContext,
} from 'react-aria-components/Toast';
import { Button } from '../button';
import { Notice } from './';
import { NoticeEventTypes } from './events';
import styles from './styles.module.css';
import { matchesMetadata } from './utils';
import type {
  NoticeActionEvent,
  NoticeContent,
  NoticeDequeueEvent,
  NoticeListProps,
  NoticeQueueEvent,
} from './types';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';

/**
 * NoticeList - Queue manager for displaying multiple notices
 *
 * Manages a FIFO queue of notices with automatic timeout and dismissal.
 * New notices push out older ones when the limit is reached.
 * Uses event-driven communication via the bus system.
 *
 * @example
 * ```tsx
 * <NoticeList
 *   aria-label="notifications"
 *   placement="top right"
 *   limit={3}
 * />
 * ```
 *
 * @param props - {@link NoticeListProps}
 * @param props.id - Unique identifier for targeting notices to this list.
 * @param props.classNames - CSS class names for list elements.
 * @param props.defaultColor - Default color for notices without explicit color.
 * @param props.defaultTimeout - Default timeout for notices without explicit timeout.
 * @param props.hideClearAll - Whether to hide the "Clear All" button.
 * @param props.limit - Maximum number of visible notices.
 * @param props.global - Whether to use global ToastRegion for portal rendering.
 * @param props.placement - Position of the notice list on screen.
 * @param props.size - Size variant for notices in the list.
 * @returns The rendered NoticeList component.
 */
export function NoticeList({
  id,
  classNames,
  defaultColor,
  defaultTimeout,
  hideClearAll,
  limit = 3,
  global,
  placement,
  size = 'medium',
  ...rest
}: NoticeListProps) {
  const queue = useMemo(
    () =>
      new ToastQueue<NoticeContent>({
        maxVisibleToasts: limit,
        wrapUpdate(fn) {
          if ('startViewTransition' in document) {
            document.startViewTransition(() => {
              flushSync(fn);
            });
          } else {
            fn();
          }
        },
      }),
    [limit],
  );
  const state = useToastQueue(queue);

  const [hasNotices, setHasNotices] = useState(false);
  const ref = useRef(null);
  const { regionProps } = useToastRegion(rest, queue, ref);

  const { useEmit, useOn } = useBus<
    NoticeQueueEvent | NoticeDequeueEvent | NoticeActionEvent
  >();
  const emitActionPrimary = useEmit(NoticeEventTypes.actionPrimary);
  const emitActionSecondary = useEmit(NoticeEventTypes.actionSecondary);
  const emitClose = useEmit(NoticeEventTypes.close);

  useOn(NoticeEventTypes.queue, (data) => {
    if (id ? data.payload.target === id : !data.payload.target) {
      const timeout = data.payload.timeout ?? defaultTimeout;
      queue.add(
        {
          ...data.payload,
          id: data.payload.id || uuid(),
          color: data.payload.color || defaultColor,
        },
        {
          timeout,
        },
      );
      if (timeout) {
        queue.resumeAll();
      }
    }
  });

  useOn(NoticeEventTypes.dequeue, (data) => {
    // @ts-expect-error : queue.queue exists, but is not currently documented
    const queuedNotices = queue.queue;

    const dequeue = queuedNotices.filter((toast: QueuedToast<NoticeContent>) =>
      matchesMetadata(data.payload, toast.content),
    );

    if (dequeue.length && dequeue.length === queuedNotices.length) {
      queue.clear();
    } else {
      for (const toast of dequeue) {
        queue.close(toast.key);
      }
    }
  });

  useEffect(
    () =>
      queue.subscribe(() => {
        setHasNotices(queue.visibleToasts.length > 0);
      }),
    [queue],
  );

  const children = (
    <>
      {!hideClearAll && hasNotices && (
        <Button
          className={composeRenderProps(
            classNames?.button,
            (className) => className ?? '',
          )}
          variant='outline'
          onPress={() => queue.clear()}
        >
          Clear All
        </Button>
      )}
      <ToastList
        className={composeRenderProps(classNames?.list, (className) =>
          clsx(styles.list, className),
        )}
      >
        {({ toast }: { toast: QueuedToast<NoticeContent> }) => (
          <Notice
            {...toast.content}
            key={toast.key}
            showClose={
              !(
                toast.timeout &&
                toast.content.primary &&
                toast.content.secondary
              )
            }
            classNames={classNames?.notice}
            size={size}
            onPrimaryAction={() => emitActionPrimary({ id: toast.content.id })}
            onSecondaryAction={() =>
              emitActionSecondary({ id: toast.content.id })
            }
            onClose={() => {
              toast.onClose?.();
              queue.close(toast.key);
              emitClose({ id: toast.content.id });
            }}
          />
        )}
      </ToastList>
    </>
  );

  return global ? (
    <ToastRegion
      className={clsx('group/region', styles.region, classNames?.region)}
      data-placement={placement}
      queue={queue}
    >
      {children}
    </ToastRegion>
  ) : (
    <div
      {...regionProps}
      className={clsx('group/region', styles.region, classNames?.region)}
      data-placement={placement}
      ref={ref}
    >
      <ToastStateContext.Provider value={state}>
        {children}
      </ToastStateContext.Provider>
    </div>
  );
}
